import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Library, Users, Star, BookOpen, MessageCircle, Award, ArrowLeftRight, ShieldCheck } from "lucide-react";

const features = [
  {
    icon: Library,
    title: "Your Shelf",
    description:
      "Track every book you've read, are reading, want to read, and own. Your reading life — beautifully organized, publicly celebrated.",
    color: "terra",
    borderColor: "border-l-terra",
  },
  {
    icon: MessageCircle,
    title: "Rooms",
    description:
      "Genre rooms. Book clubs. Vibe-based spaces. Join the conversations that match how you actually think about books — not just what you read, but why.",
    color: "forest",
    borderColor: "border-l-forest",
  },
  {
    icon: Award,
    title: "Reader Score",
    description:
      "Every review you write, every discussion you join, every book you share builds your reputation. Your reading life becomes identity capital.",
    color: "gold",
    borderColor: "border-l-gold",
  },
];

const secondaryFeatures = [
  {
    icon: BookOpen,
    title: "Reviews & Reactions",
    description: "Quick reactions or full reviews — share your honest take without the pressure of writing an essay.",
  },
  {
    icon: Users,
    title: "Read With People",
    description: "Find readers who want to read the same book. Form a reading group. Start together.",
  },
  {
    icon: ArrowLeftRight,
    title: "Buy, Swap & Rent",
    description: "Three ways to move every book. Pre-loved sales, credit-based swaps, and low-cost rentals. Coming in v2.",
  },
  {
    icon: ShieldCheck,
    title: "Trust Built In",
    description: "Dual rating system, escrow payments, condition grading. Every exchange protected.",
  },
  {
    icon: Star,
    title: "Discover",
    description: "Find your next great read through readers you trust — not algorithms you don't.",
  },
];

export default function Features() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-24 sm:py-32 bg-cream-dark/30"
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 sm:mb-20 max-w-3xl"
        >
          <p className="font-body text-sm uppercase tracking-[0.25em] text-terra mb-4" data-testid="features-label">
            What You'll Get
          </p>
          <h2
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-4"
            data-testid="features-heading"
          >
            Built for how you actually read
          </h2>
          <p className="font-body text-base sm:text-lg text-ink-light leading-relaxed">
            Not another Goodreads clone. A social book hub designed for the way Lagos readers
            discover, discuss, and share books — with each other.
          </p>
        </motion.div>

        {/* Primary features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15 * i }}
              data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s/g, "-")}`}
              className={`group relative bg-white border border-cream-dark rounded-2xl p-8 sm:p-10 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 border-l-4 ${feature.borderColor}`}
            >
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${
                  feature.color === "terra"
                    ? "bg-terra/10 text-terra"
                    : feature.color === "forest"
                    ? "bg-forest/10 text-forest"
                    : "bg-gold/10 text-gold"
                }`}
              >
                <feature.icon size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-ink mb-3">
                {feature.title}
              </h3>
              <p className="font-body text-base text-ink-light leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Secondary features — horizontal list */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {secondaryFeatures.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 + 0.08 * i }}
              data-testid={`secondary-feature-${feature.title.toLowerCase().replace(/\s/g, "-")}`}
              className="flex gap-4 p-5 rounded-xl bg-white/50 border border-transparent hover:border-cream-dark transition-all duration-300"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-ink/5 flex items-center justify-center text-ink-light">
                <feature.icon size={18} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-heading text-base font-semibold text-ink mb-1">
                  {feature.title}
                </h4>
                <p className="font-body text-sm text-ink-muted leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
